/*
 * The Portal CMS screen's state.
 *
 * Two save models on one screen, and that is not an accident: the front-page copy is a draft that
 * one bar commits, while each event's visibility applies the moment it is switched. The copy is
 * something you compose; a visibility switch is something you reach for during an incident, and
 * making that one wait for a save button would be wrong. The screen says which is which.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, superadminApi } from '../../../api';
import type { EventSalesMode, PortalSettingsDto } from '../../../api';
import type { PortalCmsState } from './PortalCmsScreen';

const EMPTY: PortalSettingsDto = {
  organizerSectionEnabled: true,
  organizerSectionTitle: '',
  organizerSectionSubtitle: '',
  organizerCards: [],
};

export function usePortalCms(token: string, onEventsChanged?: () => void): PortalCmsState {
  const [settings, setSettings] = useState<PortalSettingsDto>(EMPTY);
  const [saved, setSaved] = useState<PortalSettingsDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [busyEventId, setBusyEventId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await api.getPortalSettings();
      setSettings(next);
      setSaved(next);
      setLoadError('');
    } catch {
      setLoadError('โหลดการตั้งค่าหน้าแรกไม่สำเร็จ');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runEvent = useCallback(async (eventId: string, fn: () => Promise<unknown>) => {
    setBusyEventId(eventId);
    setSaveError('');
    try {
      await fn();
      onEventsChanged?.();
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : 'บันทึกการแสดงผลของอีเวนต์ไม่สำเร็จ');
    } finally {
      setBusyEventId(null);
    }
  }, [onEventsChanged]);

  return {
    settings,
    setSettings: (next) => { setSettings(next); setJustSaved(false); },
    dirty: !!saved && JSON.stringify(settings) !== JSON.stringify(saved),
    saving, justSaved, saveError,
    loading: saved === null && !loadError,
    loadError, busyEventId,
    save: async () => {
      setSaving(true);
      setSaveError('');
      setJustSaved(false);
      try {
        await superadminApi.updatePortalSettings(token, settings);
        setSaved(settings);
        setJustSaved(true);
      } catch (e) {
        // The draft is untouched on failure — this is copy someone wrote, not a value to re-fetch.
        setSaveError(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
      } finally {
        setSaving(false);
      }
    },
    discard: () => { if (saved) setSettings(saved); setSaveError(''); setJustSaved(false); },
    setEventPortal: (eventId, patch) => runEvent(eventId, () => superadminApi.updateEventPortalCms(token, eventId, patch)),
    setSalesMode: (eventId, mode: EventSalesMode) => runEvent(eventId, () => superadminApi.setEventSalesMode(token, eventId, mode)),
  };
}
